import React from "react";
import { Fingerprint, AlertTriangle } from "lucide-react";

const ASSET_SLOT_ORDER = [
  { family: "SUB", label: "Subject Line" },
  { family: "PRV", label: "Preview Text" },
  { family: "HOK", label: "Hook" },
  { family: "EML", label: "Email Body" },
  { family: "OFF", label: "Offer" },
  { family: "CTA", label: "CTA" },
  { family: "PS", label: "P.S." },
  { family: "LPG", label: "Landing Page" },
];

// DASH-EMAIL-012A Frozen Decision 14: the real Fingerprint resolution
// vocabulary (email_marketing_creatives.py's ResolutionOutcome). Language is
// deliberately non-alarmist and historical, never implying an error or a
// missing/broken record — the governed reference itself was preserved
// exactly as authored; only today's ability to fully reconstruct it is
// limited. `null` for "resolved" renders no badge at all (today's baseline).
const RESOLUTION_NOTES = {
  unresolved_asset_missing: "Historical reference preserved — the asset it points to no longer exists in the catalog.",
  unresolved_version_missing: "Historical reference preserved — that exact asset version is no longer in the catalog.",
  unresolved_version_placeholder: "Historical reference preserved as originally authored — no specific version was recorded.",
};

/**
 * EmailCreativeFingerprintPanel — DASH-EMAIL-011b, cut over in
 * DASH-EMAIL-012A to the real Creative Version Fingerprint resolution
 * (EMAIL-CATALOG-007A's `fingerprint_asset_refs`, reusing the exact
 * FingerprintAssetRef{family, asset_id, version, resolution} shape the
 * backend already exposes end-to-end since EMAIL-CATALOG-004/006A).
 *
 * Iterates `fingerprintAssetRefs` (not the raw `fingerprint.subject/preview/
 * ...` per-family slots, which carry no resolution outcome) — a slot simply
 * absent from the list (e.g. P.S. on a legacy Creative) renders nothing,
 * the same "do not fabricate the missing slot" behavior this panel already
 * had. No content-preview text is rendered for any slot: the real API
 * exposes no canonical preview field for any asset family yet (a real,
 * disclosed gap — see the 012A completion report) — showing ID/Version/
 * Resolution only, per DASH-EMAIL-012A Frozen Decision 13, rather than
 * fabricating preview text from the asset's own governed name.
 */
export default function EmailCreativeFingerprintPanel({ fingerprintAssetRefs, programId, campaignId, angleId, campaignLookup, angleLookup, creativeId, version, onNavigateToAsset }) {
  if (!fingerprintAssetRefs) return null;

  const refsByFamily = Object.fromEntries(fingerprintAssetRefs.map((ref) => [ref.family, ref]));

  return (
    <div className="space-y-3" data-testid="email-creative-fingerprint-panel">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-300 sm:grid-cols-4">
        <span>
          Program: <span className="text-slate-100">{programId || "—"}</span>
        </span>
        <span>
          Campaign: <span className="text-slate-100">{campaignLookup?.[campaignId] || campaignId}</span>
        </span>
        <span>
          Angle: <span className="text-slate-100">{angleId ? angleLookup?.[angleId] || angleId : "—"}</span>
        </span>
        <span>
          Creative Version:{" "}
          <span className="text-slate-100">
            {creativeId} v{version}
          </span>
        </span>
      </div>

      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <Fingerprint className="h-3 w-3" />8 Governed Asset Slots
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="email-creative-fingerprint-asset-grid">
          {ASSET_SLOT_ORDER.map((slot) => {
            const ref = refsByFamily[slot.family];
            if (!ref) return null;
            const isResolved = ref.resolution === "resolved";
            const note = RESOLUTION_NOTES[ref.resolution];
            return (
              <button
                key={slot.family}
                type="button"
                className={`rounded-md border p-2 text-left text-xs ${
                  isResolved ? "border-white/10 bg-white/5 hover:bg-white/10" : "border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10"
                }`}
                onClick={() => isResolved && onNavigateToAsset?.(ref.family, ref.asset_id, ref.version)}
                data-testid={`email-creative-fingerprint-slot-${slot.family}`}
                data-resolution={ref.resolution}
              >
                <span className="block text-[10px] uppercase tracking-wide text-slate-500">{slot.label}</span>
                <span className="block font-medium text-slate-100">
                  {ref.asset_id} {isResolved ? <span className="text-slate-400">v{ref.version}</span> : null}
                </span>
                {!isResolved && (
                  <span className="mt-1 flex items-start gap-1 text-[10px] text-amber-300" data-testid={`email-creative-fingerprint-slot-note-${slot.family}`}>
                    <AlertTriangle className="mt-0.5 h-2.5 w-2.5 shrink-0" />
                    <span>{note}</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
